import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { mergeTour, tourImage, videoSource, DEFAULT_TOUR } from "@/lib/tourContent";
import { Nav } from "@/pages/Landing";

function slug(t, i) {
  return `${i + 1}-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
}

function Shot({ src, title, phone }) {
  if (!src) return <div className="aspect-[16/10] rounded-2xl bg-slate-100" />;
  return (
    <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)] ${phone ? "mx-auto max-w-[280px]" : ""}`}>
      {!phone && <div className="flex gap-1.5 border-b border-slate-200 bg-slate-50 px-4 py-2.5">{[0, 1, 2].map((i) => <span key={i} className="h-2.5 w-2.5 rounded-full bg-slate-300" />)}</div>}
      <img src={tourImage(src)} alt={`${title} screen`} loading="lazy" className="block w-full" />
    </div>
  );
}

function Clip({ url, title, i }) {
  const v = videoSource(url);
  if (!v) return null;
  return (
    <div data-testid={`tour-video-${i}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)]">
      {v.kind === "file"
        ? <video src={v.src} controls playsInline preload="metadata" className="block aspect-video w-full" aria-label={`${title} video`} />
        : <iframe src={v.src} title={`${title} video`} loading="lazy" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen className="block aspect-video w-full" />}
    </div>
  );
}

function Media({ s, i }) {
  const mode = s.video && videoSource(s.video) ? s.media || "image" : "image";
  const shot = <Shot src={s.image} title={s.title} phone={s.image?.includes("staff-app")} />;
  if (mode === "video") return <Clip url={s.video} title={s.title} i={i} />;
  if (mode === "both") return <div className="grid gap-5"><Clip url={s.video} title={s.title} i={i} />{s.image && shot}</div>;
  return shot;
}

function Section({ s, i }) {
  const flip = i % 2 === 1;
  return (
    <section id={slug(s.title, i)} data-testid={`tour-section-${i}`} className="scroll-mt-24 border-t border-slate-200 py-16 lg:py-24">
      <div className={`grid items-center gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div className="ka-fade">
          <div className="flex items-baseline gap-3"><span className="font-mono text-sm text-emerald-700">{String(i + 1).padStart(2, "0")}</span>{s.eyebrow && <span className="ka-eyebrow">{s.eyebrow}</span>}</div>
          <h2 data-testid={`tour-section-title-${i}`} className="mt-3 font-display text-3xl font-extrabold text-slate-950 sm:text-4xl">{s.title}</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{s.text}</p>
        </div>
        <Media s={s} i={i} />
      </div>
    </section>
  );
}

export default function Tour() {
  const [t, setT] = useState(DEFAULT_TOUR);
  useEffect(() => {
    api.get("/public/tour").then((r) => setT(mergeTour(r.data))).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#F6F7F4]">
      <Nav />
      <header className="ka-grain border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-16 sm:px-8 lg:pt-24">
          <div className="ka-eyebrow">Product tour</div>
          <h1 data-testid="tour-title" className="mt-3 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] text-slate-950 sm:text-5xl lg:text-6xl">{t.title}</h1>
          <p data-testid="tour-intro" className="mt-5 max-w-2xl text-base text-slate-600 sm:text-lg">{t.intro}</p>
          <nav data-testid="tour-index" className="mt-10 flex flex-wrap gap-2">
            {t.sections.map((s, i) => <a key={i} href={`#${slug(s.title, i)}`} className="ka-press rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-900">{s.title}</a>)}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 sm:px-8">{t.sections.map((s, i) => <Section key={i} s={s} i={i} />)}</main>
      <section className="bg-slate-950 py-20 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-5 sm:px-8">
          <h2 className="max-w-xl font-display text-3xl font-extrabold sm:text-4xl">Ready to ditch the clipboard?</h2>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="tour-signup-link" to="/signup" className="ka-press inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400">Start free trial <ArrowRight className="h-4 w-4" /></Link>
            <Link data-testid="tour-demo-link" to="/demo" className="ka-press rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:border-white">Try the live demo</Link>
          </div>
        </div>
      </section>
      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-between gap-4 px-5 text-sm text-slate-500 sm:px-8">
          <span>© {new Date().getFullYear()} KitchenAudit.app</span>
          <Link data-testid="tour-footer-home" to="/" className="font-semibold text-slate-700 hover:underline">Back to home</Link>
        </div>
      </footer>
    </div>
  );
}
